// =============================================================================
// TEXT SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { TextComponent } from '@/types/components';

interface TextSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new TextComponent format
 */
function isNewTextFormat(component: ContentComponent): boolean {
  return (
    component.type === 'text' &&
    'content' in component &&
    typeof (component as any).content === 'object' &&
    'body' in (component as any).content
  );
}

/**
 * Text Section Component
 */
export const TextSection: React.FC<TextSectionProps> = ({ component }) => {
  if (isNewTextFormat(component)) {
    return <TextSectionNew component={component as unknown as any} />;
  }
  return <TextSectionLegacy component={component} />;
};

/**
 * New format Text Section
 */
const TextSectionNew: React.FC<{ component: TextComponent }> = ({ component }) => {
  const { id, content, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  // Build content-specific styles
  const contentStyle: React.CSSProperties = {
    textAlign: layout.alignment || 'left',
    maxWidth: display.maxWidth || 'none',
    margin: display.maxWidth ? '0 auto' : '0',
  };
  
  return (
    <section id={id} className={`content-section text-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        <div style={contentStyle}>
          {content.title && <h2 className="heading-style">{content.title}</h2>}
          <div 
            className="text-content"
            dangerouslySetInnerHTML={{ __html: content.body }}
            style={{ lineHeight: '1.7' }}
          />
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Text Section
 */
const TextSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const textAlign = metadata?.textAlign || 'left';
  const maxWidth = metadata?.maxWidth || 'none';
  
  const contentStyle: React.CSSProperties = {
    textAlign: textAlign as React.CSSProperties['textAlign'],
    maxWidth: maxWidth !== 'none' ? `${maxWidth}px` : 'none',
    margin: maxWidth !== 'none' ? '0 auto' : '0',
  };
  
  return (
    <section id={id} className="content-section text-section">
      <div className="container">
        <div style={contentStyle}>
          {title && <h2 className="heading-style">{title}</h2>}
          <div 
            className="text-content"
            dangerouslySetInnerHTML={{ __html: content }}
            style={{ lineHeight: '1.7' }}
          />
        </div>
      </div>
    </section>
  );
};

export { TextSectionNew, TextSectionLegacy };

